import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tabs,
  Tab,
} from '@material-ui/core';
import {
  Add,
  Edit,
  Archive,
  Unarchive,
  Delete,
  People,
  Settings as SettingsIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  IChatroom,
  getChannelChatroomsApi,
  getCommunityChatroomsApi,
  createChannelChatroomApi,
  createCommunityChatroomApi,
  archiveChatroomApi,
  unarchiveChatroomApi,
  updateChatroomSettingsApi,
  updateChatroomApi,
  getChatroomMembersApi,
  addChatroomMembersApi,
  removeChatroomMemberApi,
} from '../../api/chatrooms';
import { getProgramEnrollmentsApi } from '../../api';
import toastr from 'toastr';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  chatroomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
  tabContent: {
    marginTop: theme.spacing(2),
  },
}));

interface AdminChatroomManagerProps {
  programId: string;
  communityIdentifier?: string;
  channelIdentifier?: string;
}

export default function AdminChatroomManager({
  programId,
  communityIdentifier,
  channelIdentifier,
}: AdminChatroomManagerProps) {
  const classes = useStyles();
  const { user } = useSelector((s: RootState) => s.auth);

  const [chatrooms, setChatrooms] = useState<IChatroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedChatroom, setSelectedChatroom] = useState<IChatroom | null>(null);
  const [tab, setTab] = useState(0);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    roomName: '',
    accessType: 'role-based' as 'role-based' | 'specific-users',
    roleFilter: 'all-members' as 'all-members' | 'moderators' | 'admins' | 'community-admins',
    allowThreading: true,
    allowFileUploads: true,
    allowLinkUnfurling: true,
    isDefaultModeratorRoom: false,
    isDefaultCommunityAdminRoom: false,
  });

  // Members state
  const [members, setMembers] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    loadChatrooms();
  }, [channelIdentifier, communityIdentifier]);

  const loadChatrooms = async () => {
    if (!communityIdentifier) return;

    setLoading(true);
    try {
      let result;
      if (channelIdentifier) {
        result = await getChannelChatroomsApi(channelIdentifier);
      } else {
        result = await getCommunityChatroomsApi(communityIdentifier);
      }
      setChatrooms(result.data || []);
    } catch (error) {
      console.error('Error loading chatrooms:', error);
      toastr.error('Failed to load chatrooms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChatroom = async () => {
    if (!communityIdentifier) return;

    try {
      const payload = {
        programId,
        communityIdentifier,
        ...(channelIdentifier && { channelIdentifier }),
        roomName: formData.roomName,
        accessControl: {
          type: formData.accessType,
          ...(formData.accessType === 'role-based' && {
            roleFilter: formData.roleFilter,
          }),
        },
        settings: {
          allowThreading: formData.allowThreading,
          allowFileUploads: formData.allowFileUploads,
          allowLinkUnfurling: formData.allowLinkUnfurling,
          ...(channelIdentifier && {
            isDefaultModeratorRoom: formData.isDefaultModeratorRoom,
          }),
          ...(!channelIdentifier && {
            isDefaultCommunityAdminRoom: formData.isDefaultCommunityAdminRoom,
          }),
        },
      };

      if (channelIdentifier) {
        await createChannelChatroomApi(payload as any);
      } else {
        await createCommunityChatroomApi(payload as any);
      }

      toastr.success('Chatroom created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadChatrooms();
    } catch (error) {
      console.error('Error creating chatroom:', error);
      toastr.error('Failed to create chatroom');
    }
  };

  const handleArchive = async (roomId: string, isArchived: boolean) => {
    try {
      if (isArchived) {
        await unarchiveChatroomApi(roomId);
        toastr.success('Chatroom unarchived');
      } else {
        await archiveChatroomApi(roomId);
        toastr.success('Chatroom archived');
      }
      loadChatrooms();
    } catch (error) {
      console.error('Error archiving chatroom:', error);
      toastr.error('Failed to archive chatroom');
    }
  };

  const handleUpdateSettings = async () => {
    if (!selectedChatroom) return;

    try {
      await updateChatroomSettingsApi(selectedChatroom._id, {
        allowThreading: formData.allowThreading,
        allowFileUploads: formData.allowFileUploads,
        allowLinkUnfurling: formData.allowLinkUnfurling,
      });
      toastr.success('Settings updated');
      setSettingsDialogOpen(false);
      loadChatrooms();
    } catch (error) {
      console.error('Error updating settings:', error);
      toastr.error('Failed to update settings');
    }
  };

  const handleOpenMembers = async (chatroom: IChatroom) => {
    setSelectedChatroom(chatroom);
    setMembersDialogOpen(true);

    try {
      // Load members
      const membersResult = await getChatroomMembersApi(chatroom._id);
      setMembers(membersResult.data || []);

      // Load available users
      if (communityIdentifier) {
        const usersResult = await getProgramEnrollmentsApi(programId, communityIdentifier);
        setAvailableUsers(usersResult.data || []);
      }
    } catch (error) {
      console.error('Error loading members:', error);
      toastr.error('Failed to load members');
    }
  };

  const handleAddMembers = async () => {
    if (!selectedChatroom || selectedUsers.length === 0) return;

    try {
      await addChatroomMembersApi(selectedChatroom._id, selectedUsers, {
        autoSubscribe: true,
      });
      toastr.success(`Added ${selectedUsers.length} members`);
      setSelectedUsers([]);
      handleOpenMembers(selectedChatroom); // Reload members
    } catch (error) {
      console.error('Error adding members:', error);
      toastr.error('Failed to add members');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedChatroom) return;

    try {
      await removeChatroomMemberApi(selectedChatroom._id, userId);
      toastr.success('Member removed');
      handleOpenMembers(selectedChatroom); // Reload members
    } catch (error) {
      console.error('Error removing member:', error);
      toastr.error('Failed to remove member');
    }
  };

  const handleOpenSettings = (chatroom: IChatroom) => {
    setSelectedChatroom(chatroom);
    setFormData({
      ...formData,
      allowThreading: chatroom.settings.allowThreading ?? true,
      allowFileUploads: chatroom.settings.allowFileUploads ?? true,
      allowLinkUnfurling: chatroom.settings.allowLinkUnfurling ?? true,
    });
    setSettingsDialogOpen(true);
  };

  const handleOpenEdit = (chatroom: IChatroom) => {
    setSelectedChatroom(chatroom);
    setEditName(chatroom.room_name);
    setEditDescription(chatroom.room_description || '');
    setEditDialogOpen(true);
  };

  const handleUpdateChatroom = async () => {
    if (!selectedChatroom) return;

    try {
      await updateChatroomApi(selectedChatroom._id, {
        roomName: editName,
        roomDescription: editDescription,
      });
      toastr.success('Chatroom updated');
      setEditDialogOpen(false);
      loadChatrooms();
    } catch (error) {
      console.error('Error updating chatroom:', error);
      toastr.error('Failed to update chatroom');
    }
  };

  const resetForm = () => {
    setFormData({
      roomName: '',
      accessType: 'role-based',
      roleFilter: 'all-members',
      allowThreading: true,
      allowFileUploads: true,
      allowLinkUnfurling: true,
      isDefaultModeratorRoom: false,
      isDefaultCommunityAdminRoom: false,
    });
  };

  const getAccessControlLabel = (chatroom: IChatroom) => {
    if (chatroom.accessControl.type === 'role-based') {
      return chatroom.accessControl.roleFilter?.replace('-', ' ') || 'all members';
    }
    return 'specific users';
  };

  const filteredChatrooms = chatrooms.filter((room) => {
    if (tab === 0) return !room._isArchived;
    return room._isArchived;
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">
          {channelIdentifier ? 'Channel' : 'Community'} Chatrooms
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Chatroom
        </Button>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Active Chatrooms" />
        <Tab label="Archived" />
      </Tabs>

      <Box className={classes.tabContent}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredChatrooms.length === 0 ? (
          <Typography color="textSecondary" align="center">
            No {tab === 1 ? 'archived ' : ''}chatrooms found
          </Typography>
        ) : (
          filteredChatrooms.map((chatroom) => (
            <Card key={chatroom._id} className={classes.card}>
              <CardContent>
                <Box className={classes.chatroomHeader}>
                  <Box>
                    <Typography variant="h6">{chatroom.room_name}</Typography>
                    {chatroom.room_description && (
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, marginBottom: 8 }}>
                        {chatroom.room_description}
                      </Typography>
                    )}
                    <Box mt={1}>
                      <Chip
                        size="small"
                        label={chatroom.room_type}
                        className={classes.chip}
                      />
                      <Chip
                        size="small"
                        label={getAccessControlLabel(chatroom)}
                        className={classes.chip}
                        color="primary"
                      />
                      {chatroom.settings.isDefaultModeratorRoom && (
                        <Chip
                          size="small"
                          label="Default Mod Room"
                          className={classes.chip}
                          color="secondary"
                        />
                      )}
                    </Box>
                  </Box>
                  <Box className={classes.actions}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(chatroom)}
                      title="Edit Name & Description"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenMembers(chatroom)}
                      title="Manage Members"
                    >
                      <People />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenSettings(chatroom)}
                      title="Settings"
                    >
                      <SettingsIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleArchive(chatroom._id, chatroom._isArchived)}
                      title={chatroom._isArchived ? 'Unarchive' : 'Archive'}
                    >
                      {chatroom._isArchived ? <Unarchive /> : <Archive />}
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Create Chatroom Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Chatroom</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Chatroom Name"
            value={formData.roomName}
            onChange={(e) => setFormData({ ...formData, roomName: e.target.value })}
            className={classes.formControl}
          />

          <FormControl fullWidth className={classes.formControl}>
            <InputLabel>Access Type</InputLabel>
            <Select
              value={formData.accessType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  accessType: e.target.value as any,
                })
              }
            >
              <MenuItem value="role-based">Role-Based</MenuItem>
              <MenuItem value="specific-users">Specific Users</MenuItem>
            </Select>
          </FormControl>

          {formData.accessType === 'role-based' && (
            <FormControl fullWidth className={classes.formControl}>
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={formData.roleFilter}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    roleFilter: e.target.value as any,
                  })
                }
              >
                <MenuItem value="all-members">All Members</MenuItem>
                <MenuItem value="moderators">Moderators & Admins</MenuItem>
                <MenuItem value="community-admins">Community Admins & Admins</MenuItem>
                <MenuItem value="admins">Platform Admins Only</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.allowThreading}
                onChange={(e) =>
                  setFormData({ ...formData, allowThreading: e.target.checked })
                }
              />
            }
            label="Allow Threading"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowFileUploads}
                onChange={(e) =>
                  setFormData({ ...formData, allowFileUploads: e.target.checked })
                }
              />
            }
            label="Allow File Uploads"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowLinkUnfurling}
                onChange={(e) =>
                  setFormData({ ...formData, allowLinkUnfurling: e.target.checked })
                }
              />
            }
            label="Allow Link Unfurling"
          />

          {channelIdentifier && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefaultModeratorRoom}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isDefaultModeratorRoom: e.target.checked,
                    })
                  }
                />
              }
              label="Default Moderator Room"
            />
          )}

          {!channelIdentifier && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefaultCommunityAdminRoom}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isDefaultCommunityAdminRoom: e.target.checked,
                    })
                  }
                />
              }
              label="Default Community Admin Room"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateChatroom}
            color="primary"
            variant="contained"
            disabled={!formData.roomName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Chatroom Settings</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowThreading}
                onChange={(e) =>
                  setFormData({ ...formData, allowThreading: e.target.checked })
                }
              />
            }
            label="Allow Threading"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowFileUploads}
                onChange={(e) =>
                  setFormData({ ...formData, allowFileUploads: e.target.checked })
                }
              />
            }
            label="Allow File Uploads"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowLinkUnfurling}
                onChange={(e) =>
                  setFormData({ ...formData, allowLinkUnfurling: e.target.checked })
                }
              />
            }
            label="Allow Link Unfurling"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateSettings}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Chatroom Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Chatroom</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Chatroom Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className={classes.formControl}
            style={{ marginTop: 8 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            className={classes.formControl}
            multiline
            rows={3}
            placeholder="Add a description for this chatroom..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateChatroom}
            color="primary"
            variant="contained"
            disabled={!editName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Members</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Add Members
          </Typography>
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel>Select Users</InputLabel>
            <Select
              multiple
              value={selectedUsers}
              onChange={(e) => setSelectedUsers(e.target.value as string[])}
              renderValue={(selected) =>
                (selected as string[]).length + ' users selected'
              }
            >
              {availableUsers
                .filter(
                  (u) => !members.find((m) => m.room_user_id._id === u.userId)
                )
                .map((user) => (
                  <MenuItem key={user.userId} value={user.userId}>
                    {user.name || user.email || user.userId}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddMembers}
            disabled={selectedUsers.length === 0}
          >
            Add Selected Members
          </Button>

          <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
            Current Members ({members.length})
          </Typography>
          <List>
            {members.map((member) => (
              <ListItem key={member._id}>
                <ListItemText
                  primary={
                    member.room_user_id?.name ||
                    member.room_user_id?.email ||
                    'Unknown'
                  }
                  secondary={member.enrollment_role || 'member'}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveMember(member.room_user_id._id)}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
